<template>
  <section :class="{ 'is-active': value }" class="accordion">
    <div
      :id="`trigger-${accordionId}`"
      role="button"
      :aria-controls="`content-${accordionId}`"
      :aria-expanded="value ? 'true' : 'false'"
      class="accordion-header"
      @click.prevent="$emit('input', $event)"
    >
      <span v-if="$slots.iconStart" class="accordion-icon start">
        <slot name="iconStart" />
      </span>
      <span class="accordion-title">
        <slot name="title" />
      </span>
      <span v-if="$slots.subtitle" class="accordion-subtitle">
        <slot name="subtitle" />
      </span>
      <slot name="icon">
        <span class="accordion-icon end"
          ><span class="chevron-l" /><span class="chevron-r"
        /></span>
      </slot>
    </div>
    <div v-show="!value" @click.prevent="$emit('input', $event)">
      <slot name="opposite-body" />
    </div>
    <transition :name="transitionName" tag="div">
      <div
        v-show="value"
        :id="`content-${accordionId}`"
        :aria-labelledby="`trigger-${accordionId}`"
        :aria-hidden="!value"
        class="accordion-content"
      >
        <slot name="body" />
      </div>
    </transition>
  </section>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    value: boolean
    transitionName?: string
    accordionId: string
  }>(),
  {
    value: false,
    transitionName: 'slide',
  },
)

defineEmits<{
  (e: 'input', event: Event): void
}>()
</script>

<style scoped>
.accordion {
  border-top: 1px solid var(--colour-table-borders);
  border-bottom: 1px solid var(--colour-table-borders);
}

.accordion + .accordion {
  border-top: none;
  margin-top: 0;
}

.accordion-header:active {
  background: var(--colour-light-grey);
}

.accordion-header {
  color: var(--colour-text-default);
  text-decoration: none;
  min-height: 3em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.accordion-title {
  flex-grow: 1;
}

.accordion.is-active .accordion-title {
  color: var(--colour-dark-green);
  font-weight: bold;
}

.accordion-subtitle {
  font-weight: normal;
  font-size: var(--body);
  color: var(--colour-text-light);
}

.accordion-content {
  border-top: 1px dashed var(--colour-light-grey);
  padding: 1em 0;
}

:deep(.accordion-icon) {
  flex-shrink: 0;
  align-self: center;
  display: block;
}

:deep(.accordion-icon) {
  max-width: 2em;
  height: auto;
}

:deep(.accordion-icon img) {
  width: 100%;
  height: auto;
  max-height: 2.2em;
}

:deep(.accordion-icon.start) {
  max-width: 1.75em;
}

:deep(.accordion-icon.start ~ .accordion-title) {
  margin-left: 0.4em;
  display: inline-block;
}

.accordion-subtitle .item-count {
  display: inline-block;
  min-width: 1.7em;
  min-height: 1.6em;
  border-radius: 2em;
  background-color: var(--colour-pale-blue);
  padding: 0.2em;
  text-align: center;
}

:deep(.accordion-icon.end) {
  display: flex;
  align-items: center;
  width: 1.7em;
  height: 1.7em;
  margin-left: 0.5em;
}

:deep(.accordion-icon.end span) {
  display: block;
  border-radius: 0.2em;
  height: 0.2em;
  width: 100%;
  transition: transform 0.3s;
  border: 0.1em solid #999;
  background: #999;
}

:deep(.chevron-l) {
  transform: rotate(45deg) translate(0.25em, 0);
}

:deep(.chevron-r) {
  transform: rotate(-45deg) translate(-0.25em, 0);
}

.is-active > :deep(.accordion-header .accordion-icon.end .chevron-l) {
  transform: rotate(-45deg) translate(0, 0.25em);
}

.is-active > :deep(.accordion-header .accordion-icon.end .chevron-r) {
  transform: rotate(45deg) translate(0, 0.25em);
}
</style>
